<script setup lang="ts">
import { onMounted, ref } from 'vue'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { getDashboard } from '@/api/dashboard'
import { getApiErrorMessage } from '@/api/client'
import type { DashboardStats } from '@/types/dashboard'
import { formatSessaoBytes } from '@/utils/sessoes'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const loading = ref(true)
const stats = ref<DashboardStats | null>(null)

const cards = ref<Array<{
  to: string
  title: string
  value: string
  description: string
  icon: string
  variant?: string
}>>([])

function buildCards(data: DashboardStats): void {
  cards.value = [
    {
      to: '/admin/projetos',
      title: 'Projetos',
      value: String(data.projetos_count),
      description: 'Sites com gravação configurada',
      icon: 'bi-folder2-open',
    },
    {
      to: '/admin/sessoes',
      title: 'Sessões',
      value: String(data.sessoes_total),
      description: `${data.sessoes_hoje} hoje · ${data.sessoes_ativas} ativas`,
      icon: 'bi-camera-reels',
    },
    {
      to: '/admin/sessoes?com_erros=1',
      title: 'Erros',
      value: String(data.error_events_count),
      description: 'Eventos de erro registrados',
      icon: 'bi-exclamation-triangle',
      variant: data.error_events_count > 0 ? 'border-warning' : undefined,
    },
    {
      to: '/admin/sessoes',
      title: 'Storage',
      value: formatSessaoBytes(data.storage_bytes),
      description: 'Volume total de eventos',
      icon: 'bi-hdd-stack',
    },
  ]
}

const modules = [
  {
    to: '/admin/projetos',
    title: 'Projetos',
    description: 'Cadastre sites externos, API keys e campos de metadados.',
    icon: 'bi-folder2-open',
  },
  {
    to: '/admin/sessoes',
    title: 'Sessões',
    description: 'Replay de sessões gravadas com filtros por projeto e metadados.',
    icon: 'bi-camera-reels',
  },
  {
    to: '/admin/usuarios',
    title: 'Usuários',
    description: 'Gerenciar acessos Master e Admin.',
    icon: 'bi-people',
  },
]

async function load(): Promise<void> {
  loading.value = true
  try {
    const data = await getDashboard()
    stats.value = data
    buildCards(data)
  } catch (error) {
    stats.value = null
    cards.value = []
    console.error(getApiErrorMessage(error, 'Erro ao carregar dashboard'))
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <AdminLayout>
    <template #title>
      <h1 class="h5 mb-1">Painel de gestão</h1>
      <p class="text-muted small mb-0">Session Recorder</p>
    </template>

    <div v-if="loading" class="text-muted mb-4">Carregando métricas…</div>

    <div v-else-if="cards.length > 0" class="row g-3 mb-4">
      <div
        v-for="card in cards"
        :key="card.title"
        class="col-12 col-sm-6 col-lg-3"
      >
        <RouterLink
          :to="card.to"
          class="card h-100 text-decoration-none text-body shadow-sm border-0 admin-stat-card"
          :class="card.variant"
        >
          <div class="card-body">
            <div class="d-flex align-items-center gap-2 mb-2">
              <span class="admin-stat-card__icon rounded bg-dark-subtle text-dark d-inline-flex align-items-center justify-content-center">
                <i class="bi" :class="card.icon" aria-hidden="true" />
              </span>
              <span class="text-muted small">{{ card.title }}</span>
            </div>
            <div class="h4 mb-1">{{ card.value }}</div>
            <p class="text-muted small mb-0">{{ card.description }}</p>
          </div>
        </RouterLink>
      </div>
    </div>

    <h2 class="h6 text-muted mb-3">Módulos</h2>
    <div class="row g-3">
      <div
        v-for="module in modules"
        :key="module.to"
        class="col-12 col-md-6 col-lg-4"
      >
        <RouterLink
          :to="module.to"
          class="card h-100 text-decoration-none text-body shadow-sm border-0 admin-module-card"
        >
          <div class="card-body d-flex flex-column gap-2">
            <div class="d-flex align-items-center gap-2">
              <span class="admin-module-card__icon rounded bg-dark-subtle text-dark d-inline-flex align-items-center justify-content-center">
                <i class="bi" :class="module.icon" aria-hidden="true" />
              </span>
              <h3 class="h6 mb-0">{{ module.title }}</h3>
            </div>
            <p class="text-muted small mb-0">{{ module.description }}</p>
          </div>
        </RouterLink>
      </div>
    </div>
  </AdminLayout>
</template>

<style scoped>
.admin-module-card,
.admin-stat-card {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.admin-module-card:active,
.admin-stat-card:active {
  transform: scale(0.99);
}

.admin-module-card__icon,
.admin-stat-card__icon {
  width: 2.25rem;
  height: 2.25rem;
  font-size: 1.1rem;
}
</style>
